import { useEffect } from 'react';
import { Controller, FieldValues, Path } from 'react-hook-form';
import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Controls/Input';
import { FormField } from '@/components/Form/FormField';
import { FormManagementProps } from '@/type/form.types';
import { Switch } from '@/components/Controls/Switch';
import { InfosMessage } from '@/components/Message/InfosMessage';
import { IconInfoCircle } from '@tabler/icons-react';
import { setGroupRequiredFields } from '@/utils/required-fields/group.utils';
import { TitleSection } from '@/components/Header/Section/TitleSection';

export const GroupConfigsManagement = <TSchema extends FieldValues>({
  t,
  data,
  control,
  register,
  setValue,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  useEffect(() => {
    if (setValue && data) {
      setGroupRequiredFields({ setValue, data });
      setValue('companyName' as Path<TSchema>, data?.companyName ?? '');
      setValue('branchName' as Path<TSchema>, data?.branchName ?? '');
      setValue(
        'moderationMandatory' as Path<TSchema>,
        data?.moderationMandatory,
      );
      setValue('dmgApp' as Path<TSchema>, data?.dmgApp ?? false);
    }
  }, [setValue, data]);

  return (
    <form onSubmit={onSubmit}>
      <Card
        title={
          <TitleSection
            text={t('common:settings')}
            defaultIconClassName="ki-setting-2"
          />
        }
        footer={
          <Button
            buttonClassName="px-8"
            text={
              submitDisabled ? `${t('common:loading')}...` : t('common:save')
            }
            disabled={submitDisabled}
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <>
          <FormField text="" containerClassName="mb-1">
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <h3 className="font-semibold text-sm">
                {t('groupNameSpecific')}
              </h3>
            </div>
          </FormField>
          <FormField text={t('common:company')} textWrapperClassName="mt-3">
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('companyName' as Path<TSchema>))}
                errorMessage={errors && (errors.companyName?.message as string)}
                placeholder={t('common:company')}
                type="text"
              />
            </div>
          </FormField>
          <FormField text={t('common:branch')} textWrapperClassName="mt-3">
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('branchName' as Path<TSchema>))}
                errorMessage={errors && (errors.branchName?.message as string)}
                placeholder={t('common:branch')}
                type="text"
              />
            </div>
          </FormField>
          <div className="flex flex-wrap gap-5 items-center max-sm:gap-0">
            <FormField text={t('common:moderationMandatory')}>
              <Controller
                name="moderationMandatory"
                control={control}
                render={({ field }) => (
                  <Switch
                    leftTextClassName="self-start"
                    errorMessage={
                      errors && (errors.moderationMandatory?.message as string)
                    }
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                    }}
                  />
                )}
              />
            </FormField>
            <InfosMessage
              containerClassName="w-fit justify-center mb-2 bg-white max-sm:px-0"
              messageClassName="text-primary"
              icon={
                <IconInfoCircle
                  className="text-primary w-5.5 min-w-5.5 h-5.5 min-h-5.5"
                  stroke={2}
                />
              }
              message="Une fois la modération validée, la version du modérateur écrasera la version des utilistateurs."
            />
          </div>
          <FormField text={t('dmgDetectionApp')}>
            <Controller
              name="dmgApp"
              control={control}
              render={({ field }) => (
                <Switch
                  leftTextClassName="self-start"
                  errorMessage={
                    errors && (errors.moderationMandatory?.message as string)
                  }
                  checked={field.value}
                  onChange={(checked) => {
                    field.onChange(checked);
                  }}
                />
              )}
            />
          </FormField>
        </>
      </Card>
    </form>
  );
};
